import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Slider,
  Divider,
  InputAdornment,
} from '@mui/material';
import { PoolSettings, PoolSettingsUpdate } from '../../types';

interface SafetySettingsFormProps {
  settings: PoolSettings['safety_thresholds'];
  onChange: (data: PoolSettingsUpdate['safety_thresholds']) => void;
}

const SafetySettingsForm: React.FC<SafetySettingsFormProps> = ({
  settings,
  onChange,
}) => {
  const [formData, setFormData] = useState({
    max_occupancy: settings.max_occupancy,
    lifeguard_count: settings.lifeguard_count,
    max_depth: settings.max_depth,
    emergency_equipment_count: settings.emergency_equipment_count,
  });

  // settings prop이 변경되면 formData 업데이트
  useEffect(() => {
    setFormData({
      max_occupancy: settings.max_occupancy,
      lifeguard_count: settings.lifeguard_count,
      max_depth: settings.max_depth,
      emergency_equipment_count: settings.emergency_equipment_count,
    });
  }, [settings]);

  // 입력값 변경 시 formData 업데이트 및 상위 컴포넌트에 전달
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);
    
    if (!isNaN(numValue)) {
      const newFormData = {
        ...formData,
        [name]: numValue,
      };
      
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 수용 인원 슬라이더 값 변경
  const handleOccupancySliderChange = (event: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) {
      const newFormData = {
        ...formData,
        max_occupancy: newValue,
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 안전요원 수 슬라이더 값 변경
  const handleLifeguardSliderChange = (event: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) {
      const newFormData = {
        ...formData,
        lifeguard_count: newValue,
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 최대 수심 슬라이더 값 변경
  const handleDepthSliderChange = (event: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) {
      const newFormData = {
        ...formData,
        max_depth: newValue,
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 비상 장비 수 슬라이더 값 변경
  const handleEquipmentSliderChange = (event: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) {
      const newFormData = {
        ...formData,
        emergency_equipment_count: newValue,
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        안전 관리 기준 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        시설의 안전을 위한 기본 기준을 설정. 인원 수, 안전요원, 비상 장비 등의 설정값은 안전 상태 평가에 활용됨.
      </Typography>

      <Grid container spacing={3}>
        {/* 최대 수용 인원 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="최대 수용 인원" 
              subheader="안전한 이용을 위한 최대 인원 제한"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <TextField
                  name="max_occupancy"
                  label="최대 수용 인원"
                  type="number"
                  value={formData.max_occupancy}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    inputProps: { min: 1, max: 500, step: 1 },
                    endAdornment: <InputAdornment position="end">명</InputAdornment>,
                  }}
                />
                <Box sx={{ mt: 3, mx: 1 }}>
                  <Slider
                    value={formData.max_occupancy}
                    onChange={handleOccupancySliderChange}
                    min={1}
                    max={200}
                    step={1}
                    marks={[
                      { value: 1, label: '1명' },
                      { value: 100, label: '100명' },
                      { value: 200, label: '200명' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  안전 기준: 시설 면적 1㎡당 0.6명 이내가 권장됨.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 안전요원 수 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="안전요원 수" 
              subheader="필수 안전요원 배치 기준"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <TextField
                  name="lifeguard_count"
                  label="최소 안전요원 수"
                  type="number"
                  value={formData.lifeguard_count}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    inputProps: { min: 1, max: 20, step: 1 },
                    endAdornment: <InputAdornment position="end">명</InputAdornment>,
                  }}
                />
                <Box sx={{ mt: 3, mx: 1 }}>
                  <Slider
                    value={formData.lifeguard_count}
                    onChange={handleLifeguardSliderChange}
                    min={1}
                    max={10}
                    step={1}
                    marks={[
                      { value: 1, label: '1명' },
                      { value: 5, label: '5명' },
                      { value: 10, label: '10명' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  안전 기준: 시설 규모/수용인원에 따라 최소 2-3명 이상 배치가 권장됨.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 최대 수심 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="최대 수심" 
              subheader="시설 최대 깊이 설정"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <TextField
                  name="max_depth"
                  label="최대 수심"
                  type="number"
                  value={formData.max_depth}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    inputProps: { min: 0.5, max: 5, step: 0.1 },
                    endAdornment: <InputAdornment position="end">m</InputAdornment>,
                  }}
                />
                <Box sx={{ mt: 3, mx: 1 }}>
                  <Slider
                    value={formData.max_depth}
                    onChange={handleDepthSliderChange}
                    min={0.5}
                    max={5}
                    step={0.1}
                    marks={[
                      { value: 0.5, label: '0.5m' },
                      { value: 2.5, label: '2.5m' },
                      { value: 5, label: '5m' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  레저용 시설의 권장 수심은 1.2-1.5m, 경기용은 2.0m 이상.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 비상 장비 수 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="비상 장비 수" 
              subheader="구명환, 구명조끼 등 필수 비상 장비"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <TextField
                  name="emergency_equipment_count"
                  label="비상 장비 수"
                  type="number"
                  value={formData.emergency_equipment_count}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    inputProps: { min: 1, max: 50, step: 1 },
                    endAdornment: <InputAdornment position="end">개</InputAdornment>,
                  }}
                />
                <Box sx={{ mt: 3, mx: 1 }}>
                  <Slider
                    value={formData.emergency_equipment_count}
                    onChange={handleEquipmentSliderChange}
                    min={1}
                    max={20}
                    step={1}
                    marks={[
                      { value: 1, label: '1개' },
                      { value: 10, label: '10개' },
                      { value: 20, label: '20개' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  구명환, 구명조끼, 인명구조함, 응급 키트 등 필요한 비상 장비의 최소 수량을 설정.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SafetySettingsForm; 